import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import type { CalendarMeeting, CalendarProvider, CalendarResponse } from '@ai-meeting/shared'
import BriefModal from '@/components/brief/BriefModal'
import MeetingTable from '@/components/brief/MeetingTable'
import { buildBriefEligibleMeetingIds } from '@/components/brief/briefVisibility'
import DateInput from '@/components/DateInput'
import { SearchIcon } from '@/components/Icons'
import { fmt } from '@/constants/ui-locale'
import { useNowAtBoundaries } from '@/hooks/useNow'
import { useUiStrings } from '@/hooks/useUiStrings'
import {
  fetchBriefStatuses,
  toBriefMeeting,
  type Project,
} from '@/services/meetingBrief'
import { fetchProjects } from '@/services/projects'
import CalendarMeetingRow from './CalendarMeetingRow'
import {
  completeMicrosoftCalendarCallback,
  fetchMicrosoftCalendar,
  getMicrosoftCalendarConnectUrl,
} from '@/services/microsoft-calendar'
import { fetchGoogleCalendar, startGoogleCalendarConnect } from '@/services/google-calendar'

/** Rows the Home preview shows. */
const PREVIEW_ROWS = 3

/**
 * Query flag the Google consent callback comes back with (BRAI-538). Microsoft
 * hands its code to the page instead, which is why only this one is a flag.
 */
const GOOGLE_CALENDAR_RESULT = 'googleCalendar'

/** Show read-only calendar events separately from saved BrSE recordings. */
/**
 * Placeholder blocks in the shape of whatever this variant is about to render.
 *
 * The section used to say "Loading Teams calendar..." in plain text, which
 * collapsed the panel to one line and then pushed the page down when the real
 * list arrived. Matching the eventual layout keeps the page still, and matches
 * how Home and History already wait.
 *
 * Keeps the loading sentence as the accessible name: a screen reader gets the
 * same words it always did, while sighted users get the shape.
 */
function CalendarSkeleton({
  variant,
  label,
}: {
  variant: 'preview' | 'full'
  label: string
}) {
  return (
    <div className="mt-4" role="status" aria-label={label} data-testid="upcoming-calendar-skeleton">
      {variant === 'full' && (
        <div className="mb-6 grid gap-3 sm:grid-cols-[minmax(0,1fr)_200px]">
          <div className="h-10 animate-pulse rounded-lg bg-[var(--bg-tertiary)]" />
          <div className="h-10 animate-pulse rounded-lg bg-[var(--bg-tertiary)]" />
        </div>
      )}
      <div className="space-y-3">
        {Array.from({ length: variant === 'full' ? 5 : 3 }, (_, index) => (
          <div
            key={index}
            className="h-[76px] animate-pulse rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)]"
          />
        ))}
      </div>
    </div>
  )
}

/**
 * One provider's upcoming meetings. Rendered once per provider (BRAI-538):
 * which calendars a user has is no longer decided by how they signed in, since
 * a Microsoft-SSO account can link a Google calendar and wants to see both.
 */
export default function UpcomingCalendar({
  variant = 'preview',
  provider,
  withBrief = false,
}: {
  variant?: 'preview' | 'full'
  provider: CalendarProvider
  /**
   * Adds the "Brief" action to whichever rows this panel renders.
   *
   * Not tied to the variant: the Home preview offers the same action as the
   * full listing, on the same rows, and opens the same
   * modal. Panels that do not ask for it — or that have no room for a modal —
   * simply leave it off.
   */
  withBrief?: boolean
}) {
  const { t, locale } = useUiStrings()
  // Two panels on a page means two headings; a fixed id would have both
  // `aria-labelledby` attributes point at whichever rendered first.
  const titleId = useId()
  const isGoogle = provider === 'google'
  const briefEnabled = Boolean(withBrief)
  const labels = isGoogle
    ? {
        allMeetings: t.googleCalendarAllMeetings,
        hint: t.googleCalendarHint,
        connectHint: t.googleCalendarConnectHint,
        reconnectHint: t.googleCalendarReconnectHint,
        connect: t.googleCalendarConnect,
        reconnect: t.googleCalendarReconnect,
        refresh: t.googleCalendarRefresh,
        loading: t.googleCalendarLoading,
        loadFailed: t.googleCalendarLoadFailed,
        consentFailed: t.googleCalendarConsentFailed,
      }
    : {
        allMeetings: t.teamsCalendarAllMeetings,
        hint: t.teamsCalendarHint,
        connectHint: t.teamsCalendarConnectHint,
        reconnectHint: t.teamsCalendarReconnectHint,
        connect: t.teamsCalendarConnect,
        reconnect: t.teamsCalendarReconnect,
        refresh: t.teamsCalendarRefresh,
        loading: t.teamsCalendarLoading,
        loadFailed: t.teamsCalendarLoadFailed,
        consentFailed: t.teamsCalendarConsentFailed,
      }
  const [calendar, setCalendar] = useState<CalendarResponse>({
    status: 'CONNECTION_REQUIRED',
    items: [],
  })
  /** Full provider week; preview display may show fewer rows. */
  const [weekItems, setWeekItems] = useState<CalendarMeeting[]>([])
  const [briefProjects, setBriefProjects] = useState<Project[]>([])
  const [storedProjectKeyByEventId, setStoredProjectKeyByEventId] = useState(
    () => new Map<string, string>()
  )
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [query, setQuery] = useState('')
  const [dateFilter, setDateFilter] = useState('')
  const [briefMeeting, setBriefMeeting] = useState<CalendarMeeting | null>(null)
  /**
   * Brief appears when a meeting starts and goes away when it ends, so those
   * instants have to wake the clock; a plain minute tick would leave a finished
   * meeting offering Brief for up to a minute.
   */
  const boundarySource = briefEnabled && weekItems.length > 0 ? weekItems : calendar.items
  const boundaries = useMemo(
    () =>
      boundarySource
        .flatMap((item) => [Date.parse(item.startsAt), Date.parse(item.endsAt)])
        .filter((at) => Number.isFinite(at)),
    [boundarySource]
  )
  const now = useNowAtBoundaries(boundaries, 60_000)
  /** Remembers the last picker choice when no brief is stored yet. */
  const briefProjectByMeeting = useRef(new Map<string, Project>())
  const requestVersion = useRef(0)
  const invalidateRequests = useCallback(() => {
    requestVersion.current++
  }, [])
  /** Load independently from recordings; only page entry redeems a consent callback. */
  const load = useCallback(
    async (completeCallback = false) => {
      const version = ++requestVersion.current
      setLoading(true)
      setError(null)
      try {
        let consentFailed = false
        if (completeCallback) {
          if (isGoogle) {
            // Google's callback is server-side: it stores the grant and comes
            // back with nothing but a verdict, so there is no code to redeem
            // here — only a failure to report.
            const params = new URLSearchParams(window.location.search)
            consentFailed = params.get(GOOGLE_CALENDAR_RESULT) === 'error'
            if (params.has(GOOGLE_CALENDAR_RESULT)) {
              // Scrubbed once read, or a reload would report a failure the
              // user has already been told about and may have since fixed.
              params.delete(GOOGLE_CALENDAR_RESULT)
              const query = params.toString()
              window.history.replaceState(
                null,
                '',
                `${window.location.pathname}${query ? `?${query}` : ''}`
              )
            }
          } else {
            try {
              await completeMicrosoftCalendarCallback()
            } catch {
              consentFailed = true
            }
          }
        }
        const fetchCalendar = isGoogle ? fetchGoogleCalendar : fetchMicrosoftCalendar
        const limit = variant === 'preview' ? PREVIEW_ROWS : undefined
        const [data, weekData] =
          variant === 'preview'
            ? await Promise.all([fetchCalendar(limit), fetchCalendar()])
            : [await fetchCalendar(), null]
        if (version !== requestVersion.current) return
        setCalendar(data)
        const nextWeekItems =
          data.status === 'ACTIVE'
            ? variant === 'preview'
              ? weekData?.status === 'ACTIVE'
                ? weekData.items
                : []
              : data.items
            : []
        setWeekItems(nextWeekItems)
        setPage(0)
        if (briefEnabled && nextWeekItems.length > 0) {
          try {
            const eventIds = nextWeekItems.map((item) => item.id)
            const [statuses, projectResponse] = await Promise.all([
              fetchBriefStatuses(provider, eventIds),
              fetchProjects(),
            ])
            if (version !== requestVersion.current) return
            const stored = new Map<string, string>()
            for (const row of statuses) {
              if (row.projectKey) stored.set(row.eventId, row.projectKey)
            }
            setStoredProjectKeyByEventId(stored)
            setBriefProjects(
              projectResponse.projects.map((item) => ({
                projectKey: item.projectKey,
                name: item.name,
              }))
            )
          } catch {
            if (version === requestVersion.current) {
              setStoredProjectKeyByEventId(new Map())
              setBriefProjects([])
            }
          }
        } else if (version === requestVersion.current) {
          setStoredProjectKeyByEventId(new Map())
          setBriefProjects([])
        }
        if (consentFailed) setError(labels.consentFailed)
      } catch {
        if (version === requestVersion.current) setError(labels.loadFailed)
      } finally {
        if (version === requestVersion.current) setLoading(false)
      }
    },
    [briefEnabled, isGoogle, labels.consentFailed, labels.loadFailed, provider, variant]
  )
  useEffect(() => {
    void load(variant === 'preview')
    return invalidateRequests
  }, [invalidateRequests, load, variant])
  /** Navigate only after PKCE is safely stored in this tab. */
  async function connect() {
    setBusy(true)
    setError(null)
    try {
      window.location.assign(
        isGoogle ? await startGoogleCalendarConnect() : await getMicrosoftCalendarConnectUrl()
      )
    } catch {
      setError(labels.consentFailed)
      setBusy(false)
    }
  }
  const search = query.trim().toLocaleLowerCase(locale)
  const sortedItems = useMemo(
    () => [...calendar.items].sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt)),
    [calendar.items]
  )
  const filteredItems = sortedItems.filter((item) => {
    const matchesSearch = `${item.title} ${item.organizer}`
      .toLocaleLowerCase(locale)
      .includes(search)
    // Compare in the browser's local timezone, matching the displayed meeting time.
    const matchesDate =
      !dateFilter ||
      new Date(item.startsAt).toDateString() === new Date(`${dateFilter}T00:00:00`).toDateString()
    return matchesSearch && matchesDate
  })
  /** Whole provider week, so search or a later page cannot move Brief. */
  const briefEligibleIds = useMemo(() => {
    if (!briefEnabled || weekItems.length === 0) return new Set<string>()
    return buildBriefEligibleMeetingIds(
      weekItems,
      new Date(now),
      storedProjectKeyByEventId,
      briefProjects
    )
  }, [briefEnabled, briefProjects, now, storedProjectKeyByEventId, weekItems])
  const totalPages = Math.max(1, Math.ceil(filteredItems.length / 10))
  const currentPage = Math.min(page, totalPages - 1)
  const visibleItems =
    variant === 'preview'
      ? filteredItems.slice(0, PREVIEW_ROWS)
      : filteredItems.slice(currentPage * 10, (currentPage + 1) * 10)
  const firstPageButton = Math.max(0, Math.min(currentPage - 2, totalPages - 5))
  return (
    <section
      className={variant === 'preview' ? 'mt-8' : ''}
      aria-labelledby={titleId}
      aria-busy={loading || busy}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2
          id={titleId}
          className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--text-secondary)]"
        >
          {/* Named by provider in both variants since BRAI-538: two panels sit
              on Home now, and "Upcoming" twice says nothing about either. */}
          {labels.allMeetings}
        </h2>
        <div className="flex flex-wrap gap-3 text-xs font-semibold">
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading || busy}
            className="text-[var(--accent)] disabled:opacity-50"
          >
            {labels.refresh}
          </button>
          {variant === 'preview' && (
            <Link to="/upcoming" className="text-[var(--accent)]">
              {t.viewAll}
            </Link>
          )}
        </div>
      </div>
      <p className="mt-2 text-xs text-[var(--text-secondary)]">{labels.hint}</p>
      {error && (
        <p role="alert" className="mt-3 text-sm text-red-600">
          {error}
        </p>
      )}
      {loading ? (
        <CalendarSkeleton variant={variant} label={labels.loading} />
      ) : calendar.status !== 'ACTIVE' ? (
        <div className="mt-4 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] p-5">
          <p className="text-sm text-[var(--text-secondary)]">
            {calendar.status === 'REAUTH_REQUIRED' ? labels.reconnectHint : labels.connectHint}
          </p>
          <button
            type="button"
            onClick={() => void connect()}
            disabled={busy}
            className="btn-primary mt-3 px-4 py-2 text-sm disabled:opacity-50"
          >
            {calendar.status === 'REAUTH_REQUIRED' ? labels.reconnect : labels.connect}
          </button>
        </div>
      ) : variant === 'preview' && calendar.items.length === 0 ? (
        <p className="mt-4 rounded-lg border border-dashed border-[var(--border-color)] p-5 text-sm">
          {t.noUpcomingMeetings}
        </p>
      ) : variant === 'preview' ? (
        <div className="mt-4 space-y-3">
          {visibleItems.map((item) => (
            <CalendarMeetingRow
              key={item.id}
              meeting={item}
              provider={provider}
              briefEligibleIds={briefEligibleIds}
              onOpenBrief={briefEnabled ? (meeting) => setBriefMeeting(meeting) : undefined}
            />
          ))}
        </div>
      ) : (
        <>
          <div className="mt-6 grid gap-3 sm:grid-cols-[minmax(0,1fr)_200px]">
            <label className="relative block">
              <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-tertiary)]" />
              <input
                type="search"
                aria-label={t.searchPlaceholder}
                placeholder={t.searchPlaceholder}
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value)
                  setPage(0)
                }}
                className="input-field pl-10"
              />
            </label>
            <DateInput
              value={dateFilter}
              onChange={(value) => {
                setDateFilter(value)
                setPage(0)
              }}
              ariaLabel={t.filterByDate}
              placeholder={t.datePlaceholder}
            />
          </div>
          <div className="mt-6 overflow-hidden rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] shadow-sm">
            <MeetingTable
              items={visibleItems}
              provider={provider}
              onOpenBrief={briefEnabled ? (meeting) => setBriefMeeting(meeting) : undefined}
              briefEligibleIds={briefEligibleIds}
              emptyLabel={query || dateFilter ? t.noMeetingsFound : t.noUpcomingMeetings}
              tableLabel={labels.allMeetings}
            />
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--border-color)] px-4 py-3 text-xs text-[var(--text-secondary)] sm:px-5">
              <p>
                {filteredItems.length === 0
                  ? t.showingRangeEmpty
                  : fmt(t.showingRange, {
                      start: currentPage * 10 + 1,
                      end: Math.min((currentPage + 1) * 10, filteredItems.length),
                      total: filteredItems.length,
                    })}
              </p>
              <nav aria-label={t.upcomingSectionTitle} className="flex items-center gap-1">
                <button
                  type="button"
                  aria-label={t.previousPage}
                  onClick={() => setPage(currentPage - 1)}
                  disabled={currentPage === 0}
                  className="h-8 w-8 rounded-md border border-[var(--border-color)] disabled:opacity-50"
                >
                  ‹
                </button>
                {Array.from(
                  { length: Math.min(5, totalPages) },
                  (_, index) => firstPageButton + index
                ).map((pageNumber) => (
                  <button
                    key={pageNumber}
                    type="button"
                    aria-label={fmt(t.pageN, { n: pageNumber + 1 })}
                    aria-current={currentPage === pageNumber ? 'page' : undefined}
                    onClick={() => setPage(pageNumber)}
                    className={`h-8 w-8 rounded-md border ${currentPage === pageNumber ? 'border-[var(--accent)] bg-[var(--accent)] text-white' : 'border-[var(--border-color)]'}`}
                  >
                    {pageNumber + 1}
                  </button>
                ))}
                <button
                  type="button"
                  aria-label={t.nextPage}
                  onClick={() => setPage(currentPage + 1)}
                  disabled={currentPage >= totalPages - 1}
                  className="h-8 w-8 rounded-md border border-[var(--border-color)] disabled:opacity-50"
                >
                  ›
                </button>
              </nav>
            </div>
          </div>
        </>
      )}

      {briefMeeting && (
        <BriefModal
          key={`brief-${provider}-${briefMeeting.id}`}
          meeting={toBriefMeeting(briefMeeting, provider)}
          initialProject={briefProjectByMeeting.current.get(briefMeeting.id) ?? null}
          onProjectChange={(project) => {
            briefProjectByMeeting.current.set(briefMeeting.id, project)
          }}
          onClose={() => setBriefMeeting(null)}
        />
      )}
    </section>
  )
}
